import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { ListenedRelease } from '../types';
import { useAppContext } from '../context/AppContext';
import HistoryListItem from '../components/HistoryListItem';
import SearchBar from '../components/SearchBar';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

const History: React.FC = () => {
  const [releases, setReleases] = useState<ListenedRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'recent' | 'alphabetical' | 'by_label'>('recent');
  const [processingReleaseId, setProcessingReleaseId] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { userId, toggleListen } = useAppContext();
  const observer = useRef<IntersectionObserver | null>(null);
  const lastReleaseElementRef = useRef<HTMLDivElement | null>(null);

  const fetchTotalCount = useCallback(async (): Promise<void> => {
    try {
      const { data: count, error: countError } = await supabase
        .rpc('get_listened_releases_count', {
          p_user_id: userId,
          p_search_term: searchTerm || null,
          p_label_id: null
        });

      if (countError) {
        console.error('Error fetching count:', countError);
        return;
      }

      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  }, [userId, searchTerm]);

  const fetchListenedReleases = useCallback(async (currentOffset: number, isLoadMore: boolean = false): Promise<void> => {
    try {
      if (!isLoadMore) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      // Fetch total count on first load
      if (!isLoadMore) {
        await fetchTotalCount();
      }

      const { data: listenedReleases, error: releasesError } = await supabase
        .rpc('get_listened_releases', {
          p_user_id: userId,
          p_search_term: searchTerm || null,
          p_label_id: null,
          p_sort_order: sortOrder,
          p_limit: PAGE_SIZE,
          p_offset: currentOffset
        });

      if (releasesError) {
        console.error('RPC error:', releasesError);
        throw releasesError;
      }

      if (!listenedReleases || listenedReleases.length === 0) {
        setHasMore(false);
        if (!isLoadMore) {
          setReleases([]);
        }
        return;
      }

      if (listenedReleases.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setReleases(prev => isLoadMore ? [...prev, ...listenedReleases] : listenedReleases);

    } catch (error) {
      console.error('Error fetching listened releases:', error);
      if (error instanceof Error) {
        setError('Failed to load listening history: ' + error.message);
      } else {
        setError('Failed to load listening history');
      }
      toast.error('Failed to load listening history');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, searchTerm, sortOrder, fetchTotalCount]);

  // Fetch on mount and when search/sort changes
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchListenedReleases(0, false);
  }, [searchTerm, sortOrder, fetchListenedReleases]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    // Don't set up observer if loading, no releases, or no more to load
    if (loading || loadingMore || releases.length === 0 || !hasMore) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && releases.length > 0) {
        const newOffset = offset + PAGE_SIZE;
        setOffset(newOffset);
        fetchListenedReleases(newOffset, true);
      }
    });

    if (lastReleaseElementRef.current) {
      observer.current.observe(lastReleaseElementRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loading, loadingMore, hasMore, offset, releases.length, fetchListenedReleases]);

  const handleUnlisten = useCallback(async (releaseId: number) => {
    try {
      setProcessingReleaseId(releaseId);

      // Call toggleListen with true (currently listened) to mark as unlistened
      await toggleListen(releaseId, true);

      // Optimistically remove from the list and decrement count
      setReleases(prev => prev.filter(r => r.id !== releaseId));
      setTotalCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as unheard:', err);
      // Error toast is already handled by toggleListen context
    } finally {
      setProcessingReleaseId(null);
    }
  }, [toggleListen]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Listening History</h1>
        {!loading && (
          <p className="text-sm text-gray-600 mt-1">
            {searchTerm
              ? `${totalCount} release${totalCount !== 1 ? 's' : ''} found`
              : `${totalCount} total release${totalCount !== 1 ? 's' : ''} listened`
            }
          </p>
        )}
      </div>

      {/* Search & Filters */}
      <div className="mb-6 space-y-3">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search releases, artists, labels..."
          variant="violet"
        />
        <div className="flex gap-3">
          <div className="relative">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'recent' | 'alphabetical' | 'by_label')}
              className="appearance-none px-4 py-2.5 pr-10 border border-violet-200 rounded-lg bg-violet-100/80 text-violet-900 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent cursor-pointer hover:bg-violet-100 transition-colors"
            >
              <option value="recent">Recently Listened</option>
              <option value="alphabetical">Alphabetical</option>
              <option value="by_label">By Label</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-violet-500">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      )}

      {/* Empty State */}
      {!loading && releases.length === 0 && (
        <div className="text-center py-12 px-4">
          {searchTerm ? (
            <>
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-gray-600 text-lg font-medium">No releases found</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your search terms</p>
            </>
          ) : (
            <>
              <p className="text-gray-700 text-xl font-semibold mb-2">No listening history yet</p>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                Start building your music collection! Follow labels and mark releases as listened to track your journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <a
                  href="/labels"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors shadow-sm"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Browse Labels
                </a>
                <a
                  href="/explore"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-white text-violet-600 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm border border-violet-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Explore Releases
                </a>
              </div>
            </>
          )}
        </div>
      )}

      {/* Releases List */}
      {!loading && releases.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {releases.map((release, index) => {
            const isLast = index === releases.length - 1;
            return (
              <div key={release.id} ref={isLast ? lastReleaseElementRef : null}>
                <HistoryListItem
                  release={release}
                  onUnlisten={handleUnlisten}
                  isProcessing={processingReleaseId === release.id}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Loading More Indicator */}
      {loadingMore && (
        <div className="flex justify-center items-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        </div>
      )}

      {/* End of List */}
      {!loading && !hasMore && releases.length > 0 && (
        <div className="text-center py-6 text-gray-500 text-sm">
          You've reached the end of your listening history
        </div>
      )}
    </div>
  );
};

export default History;
